/**
 * Amtliche Dokumente — Standalone-Modul.
 * Nutzer wählt zuerst ein Mietverhältnis, dann öffnet sich das
 * eingebettete OfficialDocumentsPanel.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, MagnifyingGlass, House } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import OfficialDocumentsPanel from "@/components/tenancy/OfficialDocumentsPanel";
import MessagingDialog from "@/components/MessagingDialog";

export default function AmtlicheDokumente() {
  const [tenancies, setTenancies] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  // iter145 — „Als Anlage in Editor" → MessagingDialog auf dieser Seite
  const [openMsg, setOpenMsg] = useState(false);
  const [pendingMsgAttachments, setPendingMsgAttachments] = useState(null);

  useEffect(() => {
    api.get("/tenancies").then(({ data }) => setTenancies(data || []));
  }, []);

  const filtered = tenancies.filter((t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const tenantStr = (t.tenant_names || []).join(", ").toLowerCase();
    return (
      (t.contract_number || "").toLowerCase().includes(q) ||
      tenantStr.includes(q) ||
      (t.property_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="amtliche-dokumente-page">
      <div className="flex items-center gap-3">
        <FileText size={26} className="text-slate-700" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Amtliche Dokumente</h1>
          <p className="text-sm text-slate-500 mt-1">
            16 Standard-Vorlagen — Meldewesen (Wohnungsgeberbestätigung), Sozialleistungen, Kaution,
            Mieterhöhung, Modernisierung, GEZ, Nebenkosten und Kündigungen (Eigenbedarf, Vertragsverletzung, Zahlungsrückstand).
            Wählen Sie zuerst ein Mietverhältnis.
          </p>
        </div>
      </div>

      {!selected ? (
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-3 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Mietverhältnis suchen — Vertragsnummer, Mieter oder Objekt"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="amtliche-search"
            />
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vertrag</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Mieter</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Objekt</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-500">Keine Treffer.</td></tr>
                )}
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(t)}
                    data-testid={`amtliche-tenancy-${t.id}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{t.contract_number || "—"}</td>
                    <td className="px-3 py-2">{(t.tenant_names || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      <div className="flex items-center gap-1"><House size={11} /> {t.property_name || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{t.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-4 py-2 text-sm">
            <div>
              Für Vertrag <strong className="font-mono">{selected.contract_number}</strong> · {(selected.tenant_names || []).join(", ")}
            </div>
            <button
              onClick={() => setSelected(null)}
              className="text-xs underline text-slate-600 hover:text-slate-900"
              data-testid="amtliche-change-tenancy"
            >
              Anderes Mietverhältnis wählen
            </button>
          </div>
          <OfficialDocumentsPanel
            tenancyId={selected.id}
            onAttachToEditor={(att) => {
              setPendingMsgAttachments([att.document_id]);
              setOpenMsg(true);
            }}
          />
        </div>
      )}
      <MessagingDialog
        open={openMsg}
        onOpenChange={(v) => { setOpenMsg(v); if (!v) setPendingMsgAttachments(null); }}
        context={{ tenancy_id: selected?.id }}
        defaultSubject={selected ? `Mietverhältnis ${selected.contract_number || ""}` : ""}
        initialAttachmentDocIds={pendingMsgAttachments}
      />
    </div>
  );
}
